// API Configuration for NutriSnap Dashboard
// Match main app configuration for consistent backend connection
const isDevelopment = process.env.NODE_ENV === 'development';

// Force the correct API URL for production
const API_BASE_URL = isDevelopment
  ? 'http://localhost:3002' // Local development (matches backend port)
  : 'https://magic.thealchemyofyou.co.uk'; // Live server API (Flame backend)

export const API_ENDPOINTS = {
  // Authentication (mapped to existing endpoints)
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register', 
  REFRESH_TOKEN: '/api/auth/refresh',
  
  // User Profile - using meal portfolio for user data
  PROFILE: '/api/meals/portfolio',
  UPDATE_PROFILE: '/api/meals/portfolio',
  
  // Nutrition Analytics (mapped to existing meal portfolio endpoints)
  NUTRITION_SUMMARY: '/api/meals/portfolio', // Will extract nutrition summary from portfolio
  NUTRIENT_TRENDS: '/api/meals/portfolio', // Will analyze trends from portfolio data
  GOAL_PROGRESS: '/api/meals/portfolio', // Will calculate goals from portfolio
  COMPARISON_DATA: '/api/meals/portfolio',
  
  // AI Insights (mapped to existing coaching insights)
  AI_INSIGHTS: '/api/meals/coaching-insights',
  RAG_INSIGHTS: '/api/conversation/message', // Use RAG chat for enhanced insights
  
  // Goals Management - will use portfolio data for now
  USER_GOALS: '/api/meals/portfolio',
  UPDATE_GOALS: '/api/meals/portfolio',
  GOAL_TEMPLATES: '/api/meals/portfolio',
  
  // Saved Meals (existing endpoints)
  SAVED_MEALS: '/api/saved-meals',
  ADD_SAVED_MEAL: '/api/saved-meals',
  UPDATE_SAVED_MEAL: '/api/saved-meals',
  DELETE_SAVED_MEAL: '/api/saved-meals',
  ADD_TO_TODAY: '/api/saved-meals/:id/add-to-today',
  
  // Email Reports
  WEEKLY_REPORT: '/api/meals/coaching-insights',
  DAILY_FEEDBACK: '/api/meals/coaching-insights',

  // Email Templates (coach-editable)
  EMAIL_TEMPLATES: '/api/email-templates',
  EMAIL_TEMPLATE: '/api/email-templates', // + /:slug
  EMAIL_TEMPLATE_PREVIEW: '/api/email-templates', // + /:slug/preview
  EMAIL_TEMPLATE_TEST_SEND: '/api/email-templates', // + /:slug/test-send
  
  // Coach Features (existing)
  COACH_CLIENTS: '/api/coach/clients',
  CLIENT_OVERVIEW: '/api/meals/portfolio', // Use portfolio for client overview

  // Coach Movement Viewing
  CLIENT_MOVEMENT_PROFILE: '/api/coach/clients', // + /:clientId/movement/profile
  CLIENT_MOVEMENT_ASSIGNMENTS: '/api/coach/clients', // + /:clientId/movement/assignments
  CLIENT_MOVEMENT_COMPLETIONS: '/api/coach/clients', // + /:clientId/movement/completions
  CLIENT_MOVEMENT_HABITS: '/api/coach/clients', // + /:clientId/movement/habits
  CLIENT_MOVEMENT_STEPS: '/api/coach/clients', // + /:clientId/movement/steps
  CLIENT_MOVEMENT_LOGS: '/api/coach/clients', // + /:clientId/movement/logs
  CLIENT_MOVEMENT_MEASUREMENTS: '/api/coach/clients', // + /:clientId/movement/measurements
  
  // Meal Portfolio (existing)
  MEAL_PORTFOLIO: '/api/meals/portfolio',
  UPLOAD_IMAGE: '/api/meals/preview-analysis',

  // Meal Planning (new)
  MEAL_PLANS: '/api/meal-plans',
  MEAL_PLAN_GENERATE: '/api/meal-plans/generate',
  MEAL_PLAN_PREFERENCES: '/api/meal-plans/preferences',
  MEAL_PLAN_FEEDBACK: '/api/meal-plans/feedback',

  // Daily Comparison (planned vs actual)
  DAILY_COMPARISON: '/api/nutrition/daily-comparison',

  // Coach Supplement & Cart Management
  CLIENT_SUPPLEMENTS: '/api/coach/clients', // + /:clientId/supplements
  CLIENT_RECOMMENDATIONS: '/api/coach/clients', // + /:clientId/recommendations/ai-generate
  CLIENT_CART: '/api/coach/clients', // + /:clientId/cart
} as const;

export class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeout: number = 30000 // Default 30 second timeout
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Add security headers
    const secureHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // Security header
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers as Record<string, string>
    };

    const config: RequestInit = {
      ...options,
      headers: secureHeaders,
      credentials: 'include', // Include cookies for session management
    };

    // Add timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    config.signal = controller.signal;

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        if (response.status === 401) {
          this.clearToken();
          window.location.href = '/login';
          throw new Error('Authentication failed');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Dashboard API Request failed:', error);
      
      // Convert technical errors to user-friendly messages
      if (error instanceof Error) {
        if (error.message.includes('token') || error.message.includes('401') || error.message.includes('unauthorized')) {
          throw new Error('Your session has expired. Please log in again to continue.');
        }
        if (error.message.includes('network') || error.message.includes('fetch')) {
          throw new Error('Unable to connect. Please check your internet connection and try again.');
        }
      }
      
      throw error;
    }
  }

  // Auth methods
  async login(email: string, password: string) {
    return this.request(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(userData: any) {
    return this.request(API_ENDPOINTS.REGISTER, {
      method: 'POST', 
      body: JSON.stringify(userData),
    });
  }

  // Nutrition Analytics (adapted to use meal portfolio data)
  async getNutritionSummary(userId: string, timeframe = '7d') {
    // Use exact NutriSnap approach - get meals for exact days needed
    const daysInTimeframe = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 7;
    const portfolioData = await this.request(`${API_ENDPOINTS.NUTRITION_SUMMARY}?days=${daysInTimeframe}`);
    return this.processNutritionSummary(portfolioData, timeframe);
  }

  async getNutrientTrends(userId: string, nutrient: string, timeframe = '30d') {
    // Use meal portfolio and analyze trends client-side
    const portfolioData = await this.request(`${API_ENDPOINTS.NUTRIENT_TRENDS}`);
    return this.processNutrientTrends(portfolioData, nutrient, timeframe);
  }

  async getGoalProgress(userId: string, timeframe = '7d') {
    // Use meal portfolio and calculate goal progress client-side
    const portfolioData = await this.request(`${API_ENDPOINTS.GOAL_PROGRESS}`);
    return this.processGoalProgress(portfolioData, timeframe);
  }

  // Data processing methods for dashboard analytics
  private processNutritionSummary(portfolioData: any, timeframe: string) {
    // Use exact NutriSnap approach - backend already filtered by days, no client-side filtering
    const meals = portfolioData?.portfolio || [];
    const totalMeals = meals.length;
    
    
    // Use exact NutriSnap approach for totals calculation
    const totals = meals.reduce((acc: any, meal: any) => ({
      calories: acc.calories + (meal.totals?.calories || 0),
      protein: acc.protein + (meal.totals?.protein || 0),
      carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
      fat: acc.fat + (meal.totals?.fat || 0),
    }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0 });

    // Get days for average calculation
    const daysInTimeframe = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 7;

    
    return {
      meal_count: totalMeals,
      average_per_day: {
        calories: totalMeals > 0 ? totals.calories / daysInTimeframe : 0,
        protein: totalMeals > 0 ? totals.protein / daysInTimeframe : 0,
        carbohydrates: totalMeals > 0 ? totals.carbohydrates / daysInTimeframe : 0,
        fat: totalMeals > 0 ? totals.fat / daysInTimeframe : 0,
      }
    };
  }

  private processNutrientTrends(portfolioData: any, nutrient: string, timeframe: string) {
    // Simple trend data - could be enhanced
    return {
      nutrient,
      timeframe,
      data_points: [],
      trend: 'stable'
    };
  }

  private processGoalProgress(portfolioData: any, timeframe: string) {
    // Basic goal progress based on nutrition summary
    const summary = this.processNutritionSummary(portfolioData, timeframe);
    
    return {
      goals: [
        { name: 'Calories', target: 2000, current: summary.average_per_day.calories, progress: Math.min(100, (summary.average_per_day.calories / 2000) * 100) },
        { name: 'Protein', target: 150, current: summary.average_per_day.protein, progress: Math.min(100, (summary.average_per_day.protein / 150) * 100) },
        { name: 'Carbs', target: 250, current: summary.average_per_day.carbohydrates, progress: Math.min(100, (summary.average_per_day.carbohydrates / 250) * 100) },
        { name: 'Fat', target: 70, current: summary.average_per_day.fat, progress: Math.min(100, (summary.average_per_day.fat / 70) * 100) }
      ]
    };
  }

  private processDailyNutrition(portfolioData: any, daysAgo: number = 0) {
    // Get nutrition totals for a specific day (0 = today, 1 = yesterday, etc.)
    const allMeals = portfolioData?.portfolio || [];
    
    // Calculate the target date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - daysAgo);
    const targetDateStr = targetDate.toISOString().split('T')[0];
    
    // Filter meals for the target date
    const dayMeals = allMeals.filter((meal: any) => {
      // Try both timestamp and meal_date, prefer meal_date for actual meal date
      const mealDate = new Date(meal.meal_date || meal.timestamp || meal.created_at);
      const mealDateStr = mealDate.toISOString().split('T')[0];
      
      // Also check if the meal_date is close to target (within 1 day due to timezone)
      const targetDateObj = new Date(targetDateStr);
      const mealDateObj = new Date(mealDateStr);
      const daysDiff = Math.abs((targetDateObj.getTime() - mealDateObj.getTime()) / (1000 * 60 * 60 * 24));
      
      const matches = mealDateStr === targetDateStr || daysDiff < 1;
      
      if (daysAgo === 0) {
      }
      
      return matches;
    });
    
    if (daysAgo === 0) {
    }
    
    // Calculate totals for the day
    const totals = dayMeals.reduce((acc: any, meal: any, index: number) => {
      let calories = 0, protein = 0, carbohydrates = 0, fat = 0;
      
      // Check different possible structures (same logic as processNutritionSummary)
      if (meal.totals) {
        calories = meal.totals.calories || 0;
        protein = meal.totals.protein || 0;
        carbohydrates = meal.totals.carbohydrates || 0;
        fat = meal.totals.fat || 0;
      } else if (meal.nutrition_totals) {
        try {
          const nutritionTotals = typeof meal.nutrition_totals === 'string' 
            ? JSON.parse(meal.nutrition_totals) 
            : meal.nutrition_totals;
          
          calories = nutritionTotals.calories || nutritionTotals.energy || 0;
          protein = nutritionTotals.protein || 0;
          carbohydrates = nutritionTotals.carbohydrates || nutritionTotals.carbs || 0;
          fat = nutritionTotals.fat || nutritionTotals.total_fat || 0;
        } catch (e) {
          console.warn('Failed to parse nutrition_totals:', meal.nutrition_totals);
        }
      } else if (meal.nutrition) {
        calories = meal.nutrition.calories || 0;
        protein = meal.nutrition.protein || 0;
        carbohydrates = meal.nutrition.carbohydrates || 0;
        fat = meal.nutrition.fat || 0;
      } else if (meal.calories !== undefined) {
        calories = meal.calories || 0;
        protein = meal.protein || 0;
        carbohydrates = meal.carbohydrates || 0;
        fat = meal.fat || 0;
      }
      
      acc.calories += calories;
      acc.protein += protein;
      acc.carbohydrates += carbohydrates;
      acc.fat += fat;
      
      return acc;
    }, { calories: 0, protein: 0, carbohydrates: 0, fat: 0 });


    return {
      date: targetDateStr,
      meal_count: dayMeals.length,
      totals: {
        calories: totals.calories,
        protein: totals.protein,
        carbohydrates: totals.carbohydrates,
        fat: totals.fat,
      }
    };
  }

  // AI Insights
  async getAIInsights(userId: string, timeframe = '7d') {
    const params = new URLSearchParams({ timeframe });
    return this.request(`${API_ENDPOINTS.AI_INSIGHTS}/${userId}?${params}`);
  }

  // Micronutrients Analysis (44 nutrients)
  async getMicronutrients(userId: string, timeframe = '7d') {
    // Use meal portfolio and process comprehensive nutrition data
    const portfolioData = await this.request(`${API_ENDPOINTS.MEAL_PORTFOLIO}`);
    return this.processMicronutrientData(portfolioData, timeframe);
  }

  // Nutrition Trends Over Time 
  async getNutritionTrends(userId: string, timeframe = '30d') {
    // Use meal portfolio and create daily averages for trending
    const portfolioData = await this.request(`${API_ENDPOINTS.MEAL_PORTFOLIO}`);
    return this.processNutritionTrends(portfolioData, timeframe);
  }

  // Coach Client Methods
  async getCoachClients() {
    return this.request<{success: boolean, clients: any[]}>(API_ENDPOINTS.COACH_CLIENTS);
  }

  async getClientMeals(clientId: number, period = '30d') {
    return this.request(`/api/coach/clients/${clientId}/meals?period=${period}`);
  }

  async getClientInsights(clientId: number, days = 30) {
    return this.request(`/api/coach/clients/${clientId}/insights?days=${days}`);
  }

  // ============= COACH MOVEMENT METHODS =============

  async getClientMovementProfile(clientId: number) {
    return this.request<{
      client: { id: string; name: string; email: string; catalogueEnabled: boolean };
      stats: {
        last30Days: {
          steps: { daysLogged: number; avgSteps: number; totalSteps: number; maxSteps: number; avgGoal: number };
          workouts: { completedWorkouts: number; avgRating: number };
          assignments: { total: number; completed: number; completionRate: number };
          activeHabits: number;
        };
      };
    }>(`/api/coach/clients/${clientId}/movement/profile`);
  }

  async getClientMovementAssignments(clientId: number, options?: { startDate?: string; endDate?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params}` : '';
    return this.request<{
      assignments: Array<{
        id: number;
        userId: string;
        workoutId: number;
        scheduledDate: string;
        dayOfWeek: string | null;
        notes: string | null;
        completed: boolean;
        completedAt: string | null;
        feedbackRating: number | null;
        feedbackNotes: string | null;
        workout: {
          id: number;
          title: string;
          slug: string;
          summary: string;
          estimatedTotalSec: number;
          level: string;
          typeName: string | null;
        };
      }>;
    }>(`/api/coach/clients/${clientId}/movement/assignments${query}`);
  }

  async getClientMovementCompletions(clientId: number, options?: { startDate?: string; endDate?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params}` : '';
    return this.request<{
      completions: Array<{
        id: number;
        userId: string;
        workoutId: number;
        completedAt: string;
        feedbackRating: number | null;
        feedbackNotes: string | null;
        durationSec: number | null;
        workout: {
          id: number;
          title: string;
          slug: string;
          summary: string;
          estimatedTotalSec: number;
          level: string;
          type: { id: number; name: string; slug: string } | null;
        };
      }>;
    }>(`/api/coach/clients/${clientId}/movement/completions${query}`);
  }

  async getClientMovementHabits(clientId: number) {
    return this.request<{
      habits: Array<{
        id: number;
        userId: string;
        templateId: number | null;
        title: string;
        description: string | null;
        active: boolean;
        startedAt: string;
        completionRate: number;
        checkins: Array<{
          id: number;
          date: string;
          status: string;
          notes: string | null;
        }>;
        template: {
          id: number;
          title: string;
          description: string | null;
          frequency: string;
        } | null;
      }>;
    }>(`/api/coach/clients/${clientId}/movement/habits`);
  }

  async getClientMovementSteps(clientId: number, days = 30) {
    return this.request<{
      entries: Array<{
        id: number;
        userId: string;
        date: string;
        steps: number;
        goal: number;
        notes: string | null;
      }>;
      summary: {
        daysTracked: number;
        totalSteps: number;
        avgSteps: number;
        goalMetDays: number;
        goalMetRate: number;
      };
    }>(`/api/coach/clients/${clientId}/movement/steps?days=${days}`);
  }

  async getClientMovementLogs(clientId: number, days = 30) {
    return this.request<{
      entries: Array<{
        id: number;
        userId: string;
        date: string;
        steps: number | null;
        distanceMiles: number | null;
        stepGoal: number;
        activityType: string | null;
        activityTypes: string[];
        motivationRating: number | null;
        energyRating: number | null;
        performanceRating: number | null;
        notes: string | null;
        garminData: {
          activityType: string;
          durationSeconds: number;
          distanceMeters: number;
          distanceMiles: number;
          avgHeartRate: number;
          maxHeartRate: number;
          calories: number;
        } | null;
      }>;
      summary: {
        totalLogs: number;
        activeDays: number;
        avgMotivation: number;
        avgEnergy: number;
        avgPerformance: number;
        activityCounts: Record<string, number>;
      };
    }>(`/api/coach/clients/${clientId}/movement/logs?days=${days}`);
  }

  async getClientMovementMeasurements(clientId: number, limit = 25) {
    return this.request<{
      measurements: Array<{
        id: number;
        userId: string;
        recordedAt: string;
        weightKg: number | null;
        waistCm: number | null;
        notes: string | null;
      }>;
      trends: {
        weightChange: number | null;
        waistChange: number | null;
        periodDays: number;
      } | null;
    }>(`/api/coach/clients/${clientId}/movement/measurements?limit=${limit}`);
  }

  async getClientMicronutrients(clientId: number, timeframe = '7d') {
    // Get client meals and process micronutrient data
    const mealsData = await this.getClientMeals(clientId, timeframe);
    return this.processClientMicronutrientData(mealsData, timeframe);
  }

  async getClientNutritionTrends(clientId: number, timeframe = '30d') {
    // Get client meals and process trend data
    const mealsData = await this.getClientMeals(clientId, timeframe);
    return this.processClientNutritionTrends(mealsData, timeframe);
  }

  async getMealPortfolio(days = 30) {
    // Get raw meal portfolio data for reports
    return this.request(`${API_ENDPOINTS.MEAL_PORTFOLIO}?days=${days}`);
  }

  async getMealPortfolioByDate(date: string, clientId?: number) {
    const params = new URLSearchParams({ date });
    if (clientId) params.set('client_id', String(clientId));
    return this.request<any>(`${API_ENDPOINTS.MEAL_PORTFOLIO}?${params}`);
  }

  async getMealPortfolioByDays(days: number, clientId?: number) {
    const params = new URLSearchParams({ days: String(days) });
    if (clientId) params.set('client_id', String(clientId));
    return this.request<any>(`${API_ENDPOINTS.MEAL_PORTFOLIO}?${params}`);
  }

  async getAllTimeMicronutrients(userId: string) {
    // Get ALL meal portfolio data (use maximum allowed 365 days) and process with zero-day filtering
    const portfolioData = await this.request(`${API_ENDPOINTS.MEAL_PORTFOLIO}?days=365`);
    return this.processAllTimeMicronutrientData(portfolioData);
  }

  async getClientAllTimeMicronutrients(clientId: number) {
    // Get ALL client meals and process with zero-day filtering
    const mealsData = await this.getClientMeals(clientId, '365d');
    return this.processClientAllTimeMicronutrientData(mealsData);
  }

  // Public methods for client nutrition processing (called from components)
  async processClientNutritionSummary(mealsData: any, timeframe: string) {

    // Convert client meals to portfolio format and process
    const portfolioFormat = { portfolio: mealsData?.meals || mealsData?.portfolio || [] };
    const result = this.processNutritionSummary(portfolioFormat, timeframe);
    
    return result;
  }

  async processClientGoalProgress(mealsData: any, timeframe: string) {
    // Convert client meals to portfolio format and process goals
    const portfolioFormat = { portfolio: mealsData?.meals || mealsData?.portfolio || [] };
    return this.processGoalProgress(portfolioFormat, timeframe);
  }

  async processDailyNutritionData(mealsData: any, daysAgo: number = 0) {
    // ALWAYS use NutriSnap's simple approach - no complex date filtering
    const meals = mealsData?.meals || mealsData?.portfolio || [];
    
    // Apply NutriSnap's exact approach: simple reduce on meal.totals
    const totals = meals.reduce((acc: any, meal: any) => ({
      calories: acc.calories + (meal.totals?.calories || 0),
      protein: acc.protein + (meal.totals?.protein || 0),
      carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
      fat: acc.fat + (meal.totals?.fat || 0),
    }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0 });


    return {
      meal_count: meals.length,
      totals: {
        calories: totals.calories,
        protein: totals.protein,
        carbohydrates: totals.carbohydrates,
        fat: totals.fat,
      }
    };
  }

  private processDateSpecificNutrition(meals: any[]) {
    // EXACT NutriSnap approach - just sum meal.totals directly
    
    const totals = meals.reduce((acc: any, meal: any) => ({
      calories: acc.calories + (meal.totals?.calories || 0),
      protein: acc.protein + (meal.totals?.protein || 0),
      carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
      fat: acc.fat + (meal.totals?.fat || 0),
    }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0 });


    return {
      meal_count: meals.length,
      totals: {
        calories: totals.calories,
        protein: totals.protein,
        carbohydrates: totals.carbohydrates,
        fat: totals.fat,
      }
    };
  }

  async processClientDailyNutrition(mealsData: any, daysAgo: number = 0) {
    // Alias for processDailyNutritionData for consistency
    return this.processDailyNutritionData(mealsData, daysAgo);
  }

  async getClientMealsByDate(clientId: number, date: string) {
    // Use the same date-specific endpoint that NutriSnap uses
    // Format: /api/meals/portfolio?date=2025-09-07&userId=clientId
    const result = await this.request(`/api/meals/portfolio?date=${date}&userId=${clientId}`);
    return result;
  }

  public processMicronutrientData(portfolioData: any, timeframe: string) {
    const meals = portfolioData?.portfolio || [];
    const daysInTimeframe = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 7;
    
    
    // Aggregate all nutrition data from meals
    const nutritionTotals = meals.reduce((acc: any, meal: any) => {
      // Process macros from totals
      if (meal.totals) {
        const nutrition = meal.totals;
        acc.calories += nutrition.calories || 0;
        acc.protein += nutrition.protein || 0;
        acc.carbs += nutrition.carbs || 0;
        acc.fat += nutrition.fat || 0;
        acc.fiber += nutrition.fiber || 0;
      }
      
      // Process micronutrients from individual foods in the meal
      if (meal.foods && Array.isArray(meal.foods)) {
        meal.foods.forEach((food: any, foodIndex: number) => {
          if (food.nutrition) {
            
            // Process vitamins from food nutrition
            if (food.nutrition.vitamins) {
              Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                const value = parseFloat(food.nutrition.vitamins[vitamin]) || 0;
                acc[vitamin] = (parseFloat(acc[vitamin]) || 0) + value;
              });
            }
            
            // Process minerals from food nutrition
            if (food.nutrition.minerals) {
              Object.keys(food.nutrition.minerals).forEach(mineral => {
                const value = parseFloat(food.nutrition.minerals[mineral]) || 0;
                acc[mineral] = (parseFloat(acc[mineral]) || 0) + value;
              });
            }
            
            // Process amino acids from food nutrition
            if (food.nutrition.amino_acids) {
              Object.keys(food.nutrition.amino_acids).forEach(amino => {
                const value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                acc[amino] = (parseFloat(acc[amino]) || 0) + value;
              });
            }
            
            // Process fatty acids from food nutrition
            if (food.nutrition.fatty_acids) {
              Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                const value = parseFloat(food.nutrition.fatty_acids[fatty]) || 0;
                acc[fatty] = (parseFloat(acc[fatty]) || 0) + value;
              });
            }
          }
        });
      }
      
      return acc;
    }, {
      // Initialize with 0s for all tracked nutrients
      calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0,
      // Vitamins
      vitamin_a: 0, vitamin_c: 0, vitamin_d: 0, vitamin_e: 0, vitamin_k: 0,
      thiamine_b1: 0, riboflavin_b2: 0, niacin_b3: 0, pantothenic_acid_b5: 0,
      pyridoxine_b6: 0, biotin_b7: 0, folate_b9: 0, vitamin_b12: 0,
      // Minerals
      calcium: 0, iron: 0, magnesium: 0, zinc: 0, potassium: 0, sodium: 0,
      phosphorus: 0, copper: 0, selenium: 0, chromium: 0, molybdenum: 0, iodine: 0,
      // Amino acids
      histidine: 0, isoleucine: 0, leucine: 0, lysine: 0, methionine: 0,
      phenylalanine: 0, threonine: 0, tryptophan: 0, valine: 0,
      // Fatty acids
      saturated: 0, monounsaturated: 0, polyunsaturated: 0, omega_3: 0, omega_6: 0
    });
    
    // Calculate daily averages
    const daily_averages: any = {};
    Object.keys(nutritionTotals).forEach(nutrient => {
      daily_averages[nutrient] = nutritionTotals[nutrient] / daysInTimeframe;
    });
    
    
    return {
      meal_count: meals.length,
      daily_averages,
      timeframe
    };
  }

  private processAllTimeMicronutrientData(portfolioData: any) {
    const meals = portfolioData?.portfolio || [];
    
    // Performance monitoring for large datasets
    
    // Group meals by date to identify actual days with food logged
    const mealsByDate = new Map<string, any[]>();
    meals.forEach((meal: any) => {
      const dateKey = meal.meal_date || new Date(meal.timestamp).toISOString().split('T')[0];
      if (!mealsByDate.has(dateKey)) {
        mealsByDate.set(dateKey, []);
      }
      mealsByDate.get(dateKey)!.push(meal);
    });

    const actualDays = mealsByDate.size; // Only count days with actual meals
    
    // Aggregate all nutrition data from meals
    const nutritionTotals = meals.reduce((acc: any, meal: any) => {
      // Process macros from totals
      if (meal.totals) {
        const nutrition = meal.totals;
        acc.calories += nutrition.calories || 0;
        acc.protein += nutrition.protein || 0;
        acc.carbs += nutrition.carbs || 0;
        acc.fat += nutrition.fat || 0;
        acc.fiber += nutrition.fiber || 0;
      }
      
      // Process micronutrients from individual foods in the meal
      if (meal.foods && Array.isArray(meal.foods)) {
        meal.foods.forEach((food: any) => {
          if (food.nutrition) {
            // Process vitamins from food nutrition
            if (food.nutrition.vitamins) {
              Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                const value = parseFloat(food.nutrition.vitamins[vitamin]) || 0;
                acc[vitamin] = (parseFloat(acc[vitamin]) || 0) + value;
              });
            }
            
            // Process minerals from food nutrition
            if (food.nutrition.minerals) {
              Object.keys(food.nutrition.minerals).forEach(mineral => {
                const value = parseFloat(food.nutrition.minerals[mineral]) || 0;
                acc[mineral] = (parseFloat(acc[mineral]) || 0) + value;
              });
            }
            
            // Process amino acids from food nutrition
            if (food.nutrition.amino_acids) {
              Object.keys(food.nutrition.amino_acids).forEach(amino => {
                const value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                acc[amino] = (parseFloat(acc[amino]) || 0) + value;
              });
            }
            
            // Process fatty acids from food nutrition
            if (food.nutrition.fatty_acids) {
              Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                const value = parseFloat(food.nutrition.fatty_acids[fatty]) || 0;
                acc[fatty] = (parseFloat(acc[fatty]) || 0) + value;
              });
            }
          }
        });
      }
      
      return acc;
    }, {
      // Initialize with 0s for all tracked nutrients
      calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0,
      // Vitamins
      vitamin_a: 0, vitamin_c: 0, vitamin_d: 0, vitamin_e: 0, vitamin_k: 0,
      thiamine_b1: 0, riboflavin_b2: 0, niacin_b3: 0, pantothenic_acid_b5: 0,
      pyridoxine_b6: 0, biotin_b7: 0, folate_b9: 0, vitamin_b12: 0,
      // Minerals
      calcium: 0, iron: 0, magnesium: 0, zinc: 0, potassium: 0, sodium: 0,
      phosphorus: 0, copper: 0, selenium: 0, chromium: 0, molybdenum: 0, iodine: 0,
      // Amino acids
      histidine: 0, isoleucine: 0, leucine: 0, lysine: 0, methionine: 0,
      phenylalanine: 0, threonine: 0, tryptophan: 0, valine: 0,
      // Fatty acids
      saturated: 0, monounsaturated: 0, polyunsaturated: 0, omega_3: 0, omega_6: 0
    });
    
    // Calculate daily averages based on ACTUAL days only (excluding zero days)
    const daily_averages: any = {};
    Object.keys(nutritionTotals).forEach(nutrient => {
      daily_averages[nutrient] = actualDays > 0 ? nutritionTotals[nutrient] / actualDays : 0;
    });
    
    
    return {
      meal_count: meals.length,
      actual_days: actualDays,
      daily_averages,
      timeframe: 'all-time'
    };
  }

  async getRAGInsights(userId: string, timeframe = '7d') {
    const params = new URLSearchParams({ timeframe });
    return this.request(`${API_ENDPOINTS.RAG_INSIGHTS}/${userId}?${params}`);
  }

  // Saved Meals
  async getSavedMeals(category?: string) {
    const params = category ? new URLSearchParams({ category }) : '';
    return this.request(`${API_ENDPOINTS.SAVED_MEALS}?${params}`);
  }

  async addSavedMeal(mealData: any) {
    return this.request(API_ENDPOINTS.ADD_SAVED_MEAL, {
      method: 'POST',
      body: JSON.stringify(mealData),
    });
  }

  async updateSavedMeal(mealId: string, mealData: any) {
    return this.request(`${API_ENDPOINTS.UPDATE_SAVED_MEAL}/${mealId}`, {
      method: 'PUT',
      body: JSON.stringify(mealData),
    });
  }

  async deleteSavedMeal(mealId: string) {
    return this.request(`${API_ENDPOINTS.DELETE_SAVED_MEAL}/${mealId}`, {
      method: 'DELETE',
    });
  }

  async addMealToToday(mealId: string, mealType?: string) {
    return this.request(
      API_ENDPOINTS.ADD_TO_TODAY.replace(':id', mealId),
      {
        method: 'POST',
        body: JSON.stringify({ meal_type: mealType }),
      }
    );
  }

  // Goals Management  
  async getUserGoals(userId: string) {
    return this.request(`${API_ENDPOINTS.USER_GOALS}/${userId}`);
  }

  async updateUserGoals(userId: string, goals: any) {
    return this.request(`${API_ENDPOINTS.UPDATE_GOALS}/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ goals }),
    });
  }

  // Email Reports
  async sendWeeklyReport(userId: string, options: any = {}) {
    return this.request(API_ENDPOINTS.WEEKLY_REPORT, {
      method: 'POST',
      body: JSON.stringify({ userId, ...options }),
    });
  }

  // ============= EMAIL TEMPLATE METHODS =============

  async getEmailTemplates() {
    return this.request<{ success: boolean; templates: any[] }>(API_ENDPOINTS.EMAIL_TEMPLATES);
  }

  async getEmailTemplate(slug: string) {
    return this.request<{ success: boolean; template: any }>(`${API_ENDPOINTS.EMAIL_TEMPLATE}/${slug}`);
  }

  async updateEmailTemplate(slug: string, data: any) {
    return this.request<{ success: boolean; template: any }>(`${API_ENDPOINTS.EMAIL_TEMPLATE}/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async previewEmailTemplate(slug: string, variables?: Record<string, string>) {
    return this.request<{ success: boolean; subject: string; html: string; text: string | null }>(
      `${API_ENDPOINTS.EMAIL_TEMPLATE_PREVIEW}/${slug}/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ variables }),
      }
    );
  }

  async testSendEmailTemplate(slug: string, email: string, variables?: Record<string, string>) {
    return this.request<{ success: boolean; message: string }>(
      `${API_ENDPOINTS.EMAIL_TEMPLATE_TEST_SEND}/${slug}/test-send`,
      {
        method: 'POST',
        body: JSON.stringify({ email, variables }),
      }
    );
  }

  // Process nutrition trends for charting
  private processNutritionTrends(portfolioData: any, timeframe: string) {
    const meals = portfolioData?.portfolio || [];
    const daysInTimeframe = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
    
    if (meals.length === 0) {
      return { daily_averages: [] };
    }
    
    // Group meals by date
    const mealsByDate = new Map<string, any[]>();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysInTimeframe);
    
    meals.forEach((meal: any) => {
      const mealDate = new Date(meal.timestamp);
      if (mealDate >= cutoffDate) {
        const dateKey = mealDate.toISOString().split('T')[0];
        if (!mealsByDate.has(dateKey)) {
          mealsByDate.set(dateKey, []);
        }
        mealsByDate.get(dateKey)!.push(meal);
      }
    });
    
    // Calculate daily totals and create trend data
    const dailyTotals: any[] = [];
    const sortedDates = Array.from(mealsByDate.keys()).sort();
    
    sortedDates.forEach(date => {
      const dayMeals = mealsByDate.get(date) || [];
      const dayTotals = {
        date,
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0,
        vitamin_d: 0,
        vitamin_b12: 0,
        vitamin_c: 0,
        calcium: 0,
        iron: 0,
        sodium: 0,
        sugar: 0
      };
      
      // Sum all meals for this day
      dayMeals.forEach(meal => {
        if (meal.totals) {
          dayTotals.calories += meal.totals.calories || 0;
          dayTotals.protein += meal.totals.protein || 0;
          dayTotals.carbs += meal.totals.carbs || 0;
          dayTotals.fat += meal.totals.fat || 0;
          dayTotals.fiber += meal.totals.fiber || 0;
        }
        
        // Extract micronutrients from foods
        if (meal.foods && Array.isArray(meal.foods)) {
          meal.foods.forEach((food: any) => {
            if (food.nutrition) {
              // Vitamins
              if (food.nutrition.vitamins) {
                dayTotals.vitamin_d += parseFloat(food.nutrition.vitamins.vitamin_d) || 0;
                dayTotals.vitamin_b12 += parseFloat(food.nutrition.vitamins.vitamin_b12) || 0;
                dayTotals.vitamin_c += parseFloat(food.nutrition.vitamins.vitamin_c) || 0;
              }
              
              // Minerals
              if (food.nutrition.minerals) {
                dayTotals.calcium += parseFloat(food.nutrition.minerals.calcium) || 0;
                dayTotals.iron += parseFloat(food.nutrition.minerals.iron) || 0;
                dayTotals.sodium += parseFloat(food.nutrition.minerals.sodium) || 0;
              }
              
              // Basic nutrition
              dayTotals.sugar += parseFloat(food.nutrition.sugar) || 0;
            }
          });
        }
      });
      
      // Round values for cleaner display
      Object.keys(dayTotals).forEach(key => {
        if (key !== 'date' && typeof (dayTotals as any)[key] === 'number') {
          (dayTotals as any)[key] = Math.round((dayTotals as any)[key] * 100) / 100;
        }
      });
      
      dailyTotals.push(dayTotals);
    });
    
    return {
      daily_averages: dailyTotals,
      summary: {
        days_with_data: dailyTotals.length,
        date_range: {
          start: sortedDates[0],
          end: sortedDates[sortedDates.length - 1]
        }
      }
    };
  }

  // Client-specific data processing methods for coach API responses
  private processClientMicronutrientData(clientMealsData: any, timeframe: string) {
    
    // Handle coach API structure: {success: true, meals: [...]}
    let meals = [];
    
    if (clientMealsData?.success && clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (Array.isArray(clientMealsData)) {
      meals = clientMealsData;
    } else if (clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (clientMealsData?.data && Array.isArray(clientMealsData.data)) {
      meals = clientMealsData.data;
    } else if (clientMealsData?.portfolio && Array.isArray(clientMealsData.portfolio)) {
      meals = clientMealsData.portfolio;
    } else if (clientMealsData?.results && Array.isArray(clientMealsData.results)) {
      meals = clientMealsData.results;
    }
    
    // Convert coach API meal structure to match expected format
    meals = meals.map((meal: any) => {
      // Coach API has analyzed_foods as JSON string, convert to foods array
      if (meal.analyzed_foods && typeof meal.analyzed_foods === 'string') {
        try {
          const foods = JSON.parse(meal.analyzed_foods);
          return {
            ...meal,
            foods: Array.isArray(foods) ? foods : [foods]
          };
        } catch (e) {
          console.warn('Failed to parse analyzed_foods:', e);
          return meal;
        }
      }
      return meal;
    });
    
    const daysInTimeframe = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 7;
    
    
    if (!Array.isArray(meals) || meals.length === 0) {
      return {
        meal_count: 0,
        daily_averages: this.getZeroNutritionValues(),
        timeframe
      };
    }
    
    // Use the same processing logic but adapted for client data structure
    const nutritionTotals = meals.reduce((acc: any, meal: any, mealIndex: number) => {
      // For coach API, always calculate totals from foods since totals might not exist or be accurate
      let mealTotals = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
      
      if (meal.foods && Array.isArray(meal.foods)) {
        mealTotals = meal.foods.reduce((totals: any, food: any) => {
          if (food.nutrition) {
            totals.calories += food.nutrition.calories || 0;
            totals.protein += food.nutrition.protein || 0;
            totals.carbs += food.nutrition.carbs || 0;
            totals.fat += food.nutrition.fat || 0;
            totals.fiber += food.nutrition.fiber || 0;
          }
          return totals;
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
      }
      
      // Use calculated totals for coach API
      const nutrition = mealTotals;
      
      acc.calories += nutrition.calories || 0;
      acc.protein += nutrition.protein || 0;
      acc.carbs += nutrition.carbs || 0;
      acc.fat += nutrition.fat || 0;
      acc.fiber += nutrition.fiber || 0;
      
      
      // Process micronutrients from individual foods in the meal
      if (meal.foods && Array.isArray(meal.foods)) {
        meal.foods.forEach((food: any, foodIndex: number) => {
          if (food.nutrition) {
            
            // Process vitamins from food nutrition
            if (food.nutrition.vitamins) {
              Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                const value = parseFloat(food.nutrition.vitamins[vitamin]) || 0;
                acc[vitamin] = (parseFloat(acc[vitamin]) || 0) + value;
              });
            }
            
            // Process minerals from food nutrition
            if (food.nutrition.minerals) {
              Object.keys(food.nutrition.minerals).forEach(mineral => {
                const value = parseFloat(food.nutrition.minerals[mineral]) || 0;
                acc[mineral] = (parseFloat(acc[mineral]) || 0) + value;
              });
            }
            
            // Process amino acids from food nutrition
            if (food.nutrition.amino_acids) {
              Object.keys(food.nutrition.amino_acids).forEach(amino => {
                const value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                acc[amino] = (parseFloat(acc[amino]) || 0) + value;
              });
            }
            
            // Process fatty acids from food nutrition
            if (food.nutrition.fatty_acids) {
              Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                const value = parseFloat(food.nutrition.fatty_acids[fatty]) || 0;
                acc[fatty] = (parseFloat(acc[fatty]) || 0) + value;
              });
            }
          }
        });
      }
      
      return acc;
    }, this.getZeroNutritionValues());
    
    // Calculate daily averages
    const daily_averages: any = {};
    Object.keys(nutritionTotals).forEach(nutrient => {
      daily_averages[nutrient] = nutritionTotals[nutrient] / daysInTimeframe;
    });
    
    
    return {
      meal_count: meals.length,
      daily_averages,
      timeframe
    };
  }

  private processClientNutritionTrends(clientMealsData: any, timeframe: string) {
    
    // Handle coach API structure: {success: true, meals: [...]}
    let meals = [];
    
    if (clientMealsData?.success && clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (Array.isArray(clientMealsData)) {
      meals = clientMealsData;
    } else if (clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (clientMealsData?.data && Array.isArray(clientMealsData.data)) {
      meals = clientMealsData.data;
    } else if (clientMealsData?.portfolio && Array.isArray(clientMealsData.portfolio)) {
      meals = clientMealsData.portfolio;
    } else if (clientMealsData?.results && Array.isArray(clientMealsData.results)) {
      meals = clientMealsData.results;
    }
    
    // Convert coach API meal structure to match expected format
    meals = meals.map((meal: any) => {
      if (meal.analyzed_foods && typeof meal.analyzed_foods === 'string') {
        try {
          const foods = JSON.parse(meal.analyzed_foods);
          return {
            ...meal,
            foods: Array.isArray(foods) ? foods : [foods]
          };
        } catch (e) {
          console.warn('Failed to parse analyzed_foods in trends:', e);
          return meal;
        }
      }
      return meal;
    });
    
    const daysInTimeframeForClient = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
    
    
    if (!Array.isArray(meals) || meals.length === 0) {
      return { daily_averages: [] };
    }
    
    // Group meals by date
    const mealsByDate = new Map<string, any[]>();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysInTimeframeForClient);
    
    meals.forEach((meal: any) => {
      const mealDate = new Date(meal.timestamp || meal.meal_date);
      if (mealDate >= cutoffDate) {
        const dateKey = mealDate.toISOString().split('T')[0];
        if (!mealsByDate.has(dateKey)) {
          mealsByDate.set(dateKey, []);
        }
        mealsByDate.get(dateKey)!.push(meal);
      }
    });
    
    // Calculate daily totals and create trend data
    const dailyTotals: any[] = [];
    const sortedDates = Array.from(mealsByDate.keys()).sort();
    
    sortedDates.forEach(date => {
      const dayMeals = mealsByDate.get(date) || [];
      const dayTotals = {
        date,
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0,
        vitamin_d: 0,
        vitamin_b12: 0,
        vitamin_c: 0,
        calcium: 0,
        iron: 0,
        sodium: 0,
        sugar: 0
      };
      
      // Sum all meals for this day
      dayMeals.forEach(meal => {
        if (meal.totals) {
          dayTotals.calories += meal.totals.calories || 0;
          dayTotals.protein += meal.totals.protein || 0;
          dayTotals.carbs += meal.totals.carbs || 0;
          dayTotals.fat += meal.totals.fat || 0;
          dayTotals.fiber += meal.totals.fiber || 0;
        }
        
        // Extract micronutrients from foods
        if (meal.foods && Array.isArray(meal.foods)) {
          meal.foods.forEach((food: any) => {
            if (food.nutrition) {
              // Vitamins
              if (food.nutrition.vitamins) {
                dayTotals.vitamin_d += parseFloat(food.nutrition.vitamins.vitamin_d) || 0;
                dayTotals.vitamin_b12 += parseFloat(food.nutrition.vitamins.vitamin_b12) || 0;
                dayTotals.vitamin_c += parseFloat(food.nutrition.vitamins.vitamin_c) || 0;
              }
              
              // Minerals
              if (food.nutrition.minerals) {
                dayTotals.calcium += parseFloat(food.nutrition.minerals.calcium) || 0;
                dayTotals.iron += parseFloat(food.nutrition.minerals.iron) || 0;
                dayTotals.sodium += parseFloat(food.nutrition.minerals.sodium) || 0;
              }
              
              // Basic nutrition
              dayTotals.sugar += parseFloat(food.nutrition.sugar) || 0;
            }
          });
        }
      });
      
      // Round values for cleaner display
      Object.keys(dayTotals).forEach(key => {
        if (key !== 'date' && typeof (dayTotals as any)[key] === 'number') {
          (dayTotals as any)[key] = Math.round((dayTotals as any)[key] * 100) / 100;
        }
      });
      
      dailyTotals.push(dayTotals);
    });
    
    return {
      daily_averages: dailyTotals,
      summary: {
        days_with_data: dailyTotals.length,
        date_range: {
          start: sortedDates[0] || null,
          end: sortedDates[sortedDates.length - 1] || null
        }
      }
    };
  }

  private processClientAllTimeMicronutrientData(clientMealsData: any) {
    
    // Handle coach API structure: {success: true, meals: [...]}
    let meals = [];
    
    if (clientMealsData?.success && clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (Array.isArray(clientMealsData)) {
      meals = clientMealsData;
    } else if (clientMealsData?.meals && Array.isArray(clientMealsData.meals)) {
      meals = clientMealsData.meals;
    } else if (clientMealsData?.data && Array.isArray(clientMealsData.data)) {
      meals = clientMealsData.data;
    } else if (clientMealsData?.portfolio && Array.isArray(clientMealsData.portfolio)) {
      meals = clientMealsData.portfolio;
    } else if (clientMealsData?.results && Array.isArray(clientMealsData.results)) {
      meals = clientMealsData.results;
    }
    
    // Convert coach API meal structure to match expected format
    meals = meals.map((meal: any) => {
      if (meal.analyzed_foods && typeof meal.analyzed_foods === 'string') {
        try {
          const foods = JSON.parse(meal.analyzed_foods);
          return {
            ...meal,
            foods: Array.isArray(foods) ? foods : [foods]
          };
        } catch (e) {
          console.warn('Failed to parse analyzed_foods in all-time:', e);
          return meal;
        }
      }
      return meal;
    });
    
    // Performance monitoring for large datasets
    
    
    if (!Array.isArray(meals) || meals.length === 0) {
      return {
        meal_count: 0,
        actual_days: 0,
        daily_averages: this.getZeroNutritionValues(),
        timeframe: 'all-time'
      };
    }
    
    // Group meals by date to identify actual days with food logged
    const mealsByDate = new Map<string, any[]>();
    meals.forEach((meal: any) => {
      const dateKey = meal.meal_date || new Date(meal.timestamp).toISOString().split('T')[0];
      if (!mealsByDate.has(dateKey)) {
        mealsByDate.set(dateKey, []);
      }
      mealsByDate.get(dateKey)!.push(meal);
    });

    const actualDays = mealsByDate.size; // Only count days with actual meals
    
    // Aggregate all nutrition data from meals using same logic as regular processing
    const nutritionTotals = meals.reduce((acc: any, meal: any) => {
      // For coach API, calculate macros from foods since totals might not exist
      let mealTotals = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
      
      if (meal.foods && Array.isArray(meal.foods)) {
        mealTotals = meal.foods.reduce((totals: any, food: any) => {
          if (food.nutrition) {
            totals.calories += food.nutrition.calories || 0;
            totals.protein += food.nutrition.protein || 0;
            totals.carbs += food.nutrition.carbs || 0;
            totals.fat += food.nutrition.fat || 0;
            totals.fiber += food.nutrition.fiber || 0;
          }
          return totals;
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
      }
      
      // Use calculated totals for coach API
      const nutrition = mealTotals;
      acc.calories += nutrition.calories || 0;
      acc.protein += nutrition.protein || 0;
      acc.carbs += nutrition.carbs || 0;
      acc.fat += nutrition.fat || 0;
      acc.fiber += nutrition.fiber || 0;
      
      // Process micronutrients from individual foods in the meal
      if (meal.foods && Array.isArray(meal.foods)) {
        meal.foods.forEach((food: any) => {
          if (food.nutrition) {
            // Process vitamins from food nutrition
            if (food.nutrition.vitamins) {
              Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                const value = parseFloat(food.nutrition.vitamins[vitamin]) || 0;
                acc[vitamin] = (parseFloat(acc[vitamin]) || 0) + value;
              });
            }
            
            // Process minerals from food nutrition
            if (food.nutrition.minerals) {
              Object.keys(food.nutrition.minerals).forEach(mineral => {
                const value = parseFloat(food.nutrition.minerals[mineral]) || 0;
                acc[mineral] = (parseFloat(acc[mineral]) || 0) + value;
              });
            }
            
            // Process amino acids from food nutrition
            if (food.nutrition.amino_acids) {
              Object.keys(food.nutrition.amino_acids).forEach(amino => {
                const value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                acc[amino] = (parseFloat(acc[amino]) || 0) + value;
              });
            }
            
            // Process fatty acids from food nutrition
            if (food.nutrition.fatty_acids) {
              Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                const value = parseFloat(food.nutrition.fatty_acids[fatty]) || 0;
                acc[fatty] = (parseFloat(acc[fatty]) || 0) + value;
              });
            }
          }
        });
      }
      
      return acc;
    }, this.getZeroNutritionValues());
    
    // Calculate daily averages based on ACTUAL days only (excluding zero days)
    const daily_averages: any = {};
    Object.keys(nutritionTotals).forEach(nutrient => {
      daily_averages[nutrient] = actualDays > 0 ? nutritionTotals[nutrient] / actualDays : 0;
    });
    
    
    return {
      meal_count: meals.length,
      actual_days: actualDays,
      daily_averages,
      timeframe: 'all-time'
    };
  }

  private getZeroNutritionValues() {
    return {
      // Initialize with 0s for all tracked nutrients
      calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0,
      // Vitamins
      vitamin_a: 0, vitamin_c: 0, vitamin_d: 0, vitamin_e: 0, vitamin_k: 0,
      thiamine_b1: 0, riboflavin_b2: 0, niacin_b3: 0, pantothenic_acid_b5: 0,
      pyridoxine_b6: 0, biotin_b7: 0, folate_b9: 0, vitamin_b12: 0,
      // Minerals
      calcium: 0, iron: 0, magnesium: 0, zinc: 0, potassium: 0, sodium: 0,
      phosphorus: 0, copper: 0, selenium: 0, chromium: 0, molybdenum: 0, iodine: 0,
      // Amino acids
      histidine: 0, isoleucine: 0, leucine: 0, lysine: 0, methionine: 0,
      phenylalanine: 0, threonine: 0, tryptophan: 0, valine: 0,
      // Fatty acids
      saturated: 0, monounsaturated: 0, polyunsaturated: 0, omega_3: 0, omega_6: 0
    };
  }

  // ============= MEAL PLANNING METHODS =============
  // All methods support optional clientId for coach mode operations

  // Helper to build query params with optional client_id
  private buildMealPlanParams(params: Record<string, any> = {}, clientId?: number): string {
    const allParams = clientId ? { ...params, client_id: clientId } : params;
    const filtered = Object.entries(allParams).filter(([_, v]) => v !== undefined);
    if (filtered.length === 0) return '';
    return '?' + filtered.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  }

  // Get all meal plans for the user (or client in coach mode)
  async getMealPlans(status?: string, clientId?: number) {
    const params = this.buildMealPlanParams({ status }, clientId);
    return this.request<{success: boolean, plans: any[]}>(`${API_ENDPOINTS.MEAL_PLANS}${params}`);
  }

  // Get a specific meal plan with all details
  async getMealPlan(planId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, plan: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}${params}`);
  }

  // Generate a new AI meal plan (uses longer timeout for AI generation)
  async generateMealPlan(options: {
    startDate?: string;
    days?: number;
    mealsPerDay?: number;
    includeSnacks?: boolean;
    focusNutrients?: string[];
    clientId?: number;
  } = {}) {
    const { clientId, ...planOptions } = options;
    // Use 3 minute timeout for AI meal plan generation (chunked generation takes time)
    return this.request<{success: boolean, plan: any}>(API_ENDPOINTS.MEAL_PLAN_GENERATE, {
      method: 'POST',
      body: JSON.stringify({ ...planOptions, client_id: clientId }),
    }, 180000);
  }

  // Create a manual meal plan
  async createMealPlan(planData: {
    name?: string;
    startDate: string;
    endDate: string;
    nutrientTargets?: any;
    clientId?: number;
  }) {
    const { clientId, ...data } = planData;
    return this.request<{success: boolean, plan: any}>(API_ENDPOINTS.MEAL_PLANS, {
      method: 'POST',
      body: JSON.stringify({ ...data, client_id: clientId }),
    });
  }

  // Update a meal plan
  async updateMealPlan(planId: number, updates: any, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, plan: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}${params}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete a meal plan
  async deleteMealPlan(planId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}${params}`, {
      method: 'DELETE',
    });
  }

  // Activate a meal plan
  async activateMealPlan(planId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/activate${params}`, {
      method: 'POST',
    });
  }

  // Get a specific day from a meal plan
  async getMealPlanDay(planId: number, dayNumber: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, day: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/days/${dayNumber}${params}`);
  }

  // Update a meal entry
  async updateMealEntry(planId: number, entryId: number, updates: any, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, entry: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/entries/${entryId}${params}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Get swap alternatives for a meal
  async getSwapAlternatives(planId: number, entryId: number, count: number = 3, clientId?: number) {
    return this.request<{success: boolean, alternatives: any[]}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/entries/${entryId}/swap`, {
      method: 'POST',
      body: JSON.stringify({ count, client_id: clientId }),
    });
  }

  // Lock/unlock a meal entry
  async toggleMealLock(planId: number, entryId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, entry: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/entries/${entryId}/lock${params}`, {
      method: 'POST',
    });
  }

  // Regenerate a specific day
  async regenerateMealPlanDay(planId: number, dayNumber: number, clientId?: number) {
    return this.request<{success: boolean, day: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/days/${dayNumber}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  // Get shopping list for a meal plan
  async getShoppingList(planId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, items: any[], byCategory: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/shopping-list${params}`);
  }

  // Regenerate shopping list
  async regenerateShoppingList(planId: number, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, items: any[], byCategory: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/shopping-list/regenerate${params}`, {
      method: 'POST',
    });
  }

  // Export shopping list
  async exportShoppingList(planId: number, format: 'text' | 'markdown' = 'text', clientId?: number) {
    const params = this.buildMealPlanParams({ format }, clientId);
    return this.request<{success: boolean, content: string, format: string}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/shopping-list/export${params}`);
  }

  // Update shopping list item (check/uncheck)
  async updateShoppingListItem(planId: number, itemId: number, updates: { is_checked?: boolean; notes?: string }, clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, item: any}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/shopping-list/${itemId}${params}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Get user meal planning preferences (or client preferences in coach mode)
  async getMealPlanPreferences(clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, preferences: any}>(`${API_ENDPOINTS.MEAL_PLAN_PREFERENCES}${params}`);
  }

  // Update user meal planning preferences (or client preferences in coach mode)
  async updateMealPlanPreferences(preferences: {
    preferred_cuisines?: string[];
    excluded_cuisines?: string[];
    max_prep_time_minutes?: number;
    max_cook_time_minutes?: number;
    cooking_skill_level?: 'beginner' | 'intermediate' | 'advanced';
    meals_per_day?: number;
    include_snacks?: boolean;
    batch_cooking_preference?: boolean;
    budget_level?: 'economy' | 'moderate' | 'premium';
    seasonal_preference?: boolean;
    variety_preference?: 'low' | 'medium' | 'high';
    leftover_tolerance?: 'none' | 'next_day' | 'multi_day';
  }, clientId?: number) {
    return this.request<{success: boolean, preferences: any}>(API_ENDPOINTS.MEAL_PLAN_PREFERENCES, {
      method: 'PUT',
      body: JSON.stringify({ ...preferences, client_id: clientId }),
    });
  }

  // Submit feedback on a meal
  async submitMealFeedback(planId: number, feedback: {
    entryId?: number;
    recipeId?: number;
    feedbackType: 'swap' | 'reject' | 'favorite' | 'rating' | 'portion_adjust';
    feedbackData?: any;
    reason?: string;
  }, clientId?: number) {
    return this.request<{success: boolean}>(`${API_ENDPOINTS.MEAL_PLANS}/${planId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ ...feedback, client_id: clientId }),
    });
  }

  // Get feedback summary
  async getMealFeedbackSummary(clientId?: number) {
    const params = this.buildMealPlanParams({}, clientId);
    return this.request<{success: boolean, summary: any}>(`${API_ENDPOINTS.MEAL_PLAN_FEEDBACK}/summary${params}`);
  }

  // ============= AGENT INSIGHTS (Coach) =============

  async getCoachBriefings() {
    return this.request<{success: boolean, clients: any[]}>('/api/agent-insights/coach-briefing');
  }

  async getClientInsightDetail(clientId: string) {
    return this.request<{success: boolean, data: any}>(`/api/agent-insights/coach-briefing/${clientId}`);
  }

  async regenerateClientInsight(clientId: string) {
    return this.request<{success: boolean, data: any}>('/api/agent-insights/regenerate', {
      method: 'POST',
      body: JSON.stringify({ userId: clientId }),
    });
  }

  // ============= DAILY COMPARISON (Planned vs Actual) =============

  // Get daily comparison between planned meals and actual logged meals
  async getDailyComparison(date?: string, clientId?: number) {
    const params = this.buildMealPlanParams({ date }, clientId);
    return this.request<{
      success: boolean;
      date: string;
      activePlan: {
        id: number;
        name: string;
        startDate: string;
        endDate: string;
      } | null;
      plannedMeals: Record<string, Array<{
        id: number;
        name: string;
        recipe: any;
        nutrition: { calories: number; protein: number; carbs: number; fat: number; fiber: number };
        prepTime: number;
        cookTime: number;
      }>>;
      actualMeals: Record<string, Array<{
        id: number;
        name: string;
        foods: any[];
        nutrition: { calories: number; protein: number; carbs: number; fat: number; fiber: number };
        confidence: number;
        imagePath: string;
        timestamp: string;
      }>>;
      comparison: {
        planned: { calories: number; protein: number; carbs: number; fat: number; fiber: number };
        actual: { calories: number; protein: number; carbs: number; fat: number; fiber: number };
        variance: { calories: number; protein: number; carbs: number; fat: number; fiber: number };
      };
      adherence: {
        percentage: number | null;
        message: string;
      };
      mealTypes: string[];
    }>(`${API_ENDPOINTS.DAILY_COMPARISON}${params}`);
  }

  // =========================================================================
  // Supplement & Cart Management
  // =========================================================================

  async getClientSupplements(clientId: number) {
    return this.request<{
      success: boolean;
      supplements: Array<{
        id: string;
        name: string;
        dosage: string;
        unit: string;
        frequency: string;
        time_of_day: string | null;
        is_active: boolean;
        notes: string | null;
        nutrients: Record<string, number>;
        product_url: string | null;
        purchase_url: string | null;
        created_by: 'user' | 'coach' | 'purchase';
        coach_notes: string | null;
        product_id: string | null;
        created_at: string;
      }>;
    }>(`/api/coach/clients/${clientId}/supplements`);
  }

  async addClientSupplement(clientId: number, data: {
    name: string;
    dosage: string;
    unit: string;
    frequency: string;
    time_of_day?: string;
    notes?: string;
    nutrients?: Record<string, number>;
    coach_notes?: string;
    product_url?: string;
    purchase_url?: string;
  }) {
    return this.request<{ success: boolean; supplement_id: string; message: string }>(
      `/api/coach/clients/${clientId}/supplements`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async deleteClientSupplement(clientId: number, supplementId: string) {
    return this.request<{ success: boolean; message: string }>(
      `/api/coach/clients/${clientId}/supplements/${supplementId}`,
      { method: 'DELETE' }
    );
  }

  async generateClientRecommendations(clientId: number, options?: { maxProducts?: number; daysToAnalyze?: number }) {
    return this.request<{
      success: boolean;
      error?: string;
      message?: string;
      daysLogged?: number;
      daysRequired?: number;
      type?: string;
      analyzedPeriod?: { days: number; mealCount: number };
      nutritionSummary?: {
        adequateCount: number;
        marginalCount: number;
        deficientCount: number;
        topDeficiencies: string[];
        topMarginal: string[];
      };
      aiAnalysis?: {
        overallAssessment: string;
        keyInsights: string[];
        synergies: string[];
        cautions: string[];
      };
      recommendations?: Array<{
        id: string;
        name: string;
        brand: string;
        description: string;
        category: string;
        price: number;
        currency: string;
        aiReason: string;
        priority: number;
        addressesNutrients: string[];
        synergyWith: string[];
        matchScore: number;
      }>;
      educationalContent?: string;
    }>(`/api/coach/clients/${clientId}/recommendations/ai-generate`, {
      method: 'POST',
      body: JSON.stringify(options || {})
    });
  }

  async getClientCart(clientId: number) {
    return this.request<{
      success: boolean;
      cart: {
        item_count: number;
        items: Array<{
          id: string;
          product_id: string;
          product_name: string;
          brand: string;
          image_url: string | null;
          quantity: number;
          unit_price: number;
          item_total: number;
        }>;
        subtotal: number;
        shipping: number;
        total: number;
      };
    }>(`/api/coach/clients/${clientId}/cart`);
  }

  async addToClientCart(clientId: number, products: Array<{ product_id: string; quantity?: number }>) {
    return this.request<{
      success: boolean;
      message: string;
      added: Array<{ product_id: string; name: string; quantity: number; price: number }>;
      errors?: Array<{ product_id: string; error: string }>;
    }>(`/api/coach/clients/${clientId}/cart/add`, {
      method: 'POST',
      body: JSON.stringify({ products })
    });
  }
}

export const apiClient = new ApiClient();
export default apiClient;